import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import CountUp from "react-countup";
import JSZip from "jszip";
import {
    Tree, Waves, BatteryFull, Cpu, ShareNetwork, Copy, ArrowLeft, DownloadSimple,
    Package,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import { api, endpoints } from "@/lib/api";
import { FoundingBadge } from "@/components/dashboard/FoundingBadge";
import { copyToClipboard } from "@/lib/download";
import { buildStickerSVG } from "@/lib/svgBuilders";
import { getTierConfig } from "@/components/dashboard/FoundingBadge";

const toRoman = (num) => {
    const map = [
        [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
        [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
        [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
    ];
    let n = parseInt(num, 10) || 0;
    let out = "";
    for (const [v, s] of map) {
        while (n >= v) { out += s; n -= v; }
    }
    return out;
};

const StatTile = ({ icon: Icon, color, label, value, suffix, decimals = 0 }) => (
    <div className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-5">
        <div className="flex items-center gap-2" style={{ color }}>
            <Icon size={14} weight="fill" />
            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                {label}
            </span>
        </div>
        <div className="mt-3 font-mono-metric text-3xl font-bold text-white sm:text-4xl">
            <CountUp end={value} separator="," duration={1.6} decimals={decimals} />
            {suffix && (
                <span className="ml-2 text-sm font-normal text-zinc-500">{suffix}</span>
            )}
        </div>
    </div>
);

/**
 * Public shareable Founder profile.
 * Route: /impact/founder-:number  (e.g. /impact/founder-0047)
 */
const FounderProfile = () => {
    const { memberSlug } = useParams();
    // Accept "founder-0047", "0047", or "47"
    const rawNumber = String(memberSlug || "").replace(/^founder-/, "").replace(/^0+/, "");
    const memberNumber = parseInt(rawNumber, 10);
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let ignore = false;
        if (!memberNumber) {
            setNotFound(true);
            return;
        }
        api.get(endpoints.founderProfile(memberNumber))
            .then((r) => !ignore && setData(r.data))
            .catch(() => !ignore && setNotFound(true));
        return () => { ignore = true; };
    }, [memberNumber]);

    const handleShare = async () => {
        // Share the crawler-friendly URL so LinkedIn/X render a rich card.
        // Falls back to current page if backend URL is unavailable.
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const padded = String(data?.member_number ?? memberNumber).padStart(4, "0");
        const shareUrl =
            backend && data
                ? `${backend}/api/share/founder-${padded}`
                : typeof window !== "undefined"
                ? window.location.href
                : "";
        const ok = await copyToClipboard(shareUrl);
        if (ok) toast.success("Share link copied", { description: shareUrl });
        else toast.error("Could not copy — please copy from the address bar");
    };

    // Download the personalized founding coin PNG (only shown for founding tier).
    const handleDownloadCoin = () => {
        if (!data) return;
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const padded = String(data.member_number).padStart(4, "0");
        const url = `${backend}/api/coin/founder/${data.member_number}.png`;
        const a = document.createElement("a");
        a.href = url;
        a.download = `eviq-founder-${padded}-coin.png`;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success("Coin saved", { description: "Print it, frame it — it's yours." });
    };

    // Founder Print Pack: coin PNG + Roman-numeral car sticker (PNG + SVG) + README → ZIP
    const [busyKit, setBusyKit] = useState(false);
    const svgToPngBlob = (svgString, width = 1200, height = 1200) =>
        new Promise((resolve, reject) => {
            const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
            const svgUrl = URL.createObjectURL(svgBlob);
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(svgUrl);
                    blob ? resolve(blob) : reject(new Error("canvas toBlob failed"));
                }, "image/png");
            };
            img.onerror = () => {
                URL.revokeObjectURL(svgUrl);
                reject(new Error("svg load failed"));
            };
            img.src = svgUrl;
        });

    const handleDownloadPrintKit = async () => {
        if (busyKit || !data) return;
        setBusyKit(true);
        try {
            const backend = process.env.REACT_APP_BACKEND_URL || "";
            const padded = String(data.member_number).padStart(4, "0");
            const cfg = getTierConfig(data.founder_tier);

            const coinResp = await fetch(`${backend}/api/coin/founder/${data.member_number}.png`);
            if (!coinResp.ok) throw new Error("coin fetch failed");
            const coinBlob = await coinResp.blob();

            const stickerSvg = buildStickerSVG({
                color: cfg.color,
                tierShort: cfg.short.toUpperCase(),
                tierCap: cfg.cap.toUpperCase(),
                memberNumber: padded,
                joined: new Date(data.member_since).getFullYear(),
            });
            const stickerPng = await svgToPngBlob(stickerSvg, 1200, 1200);

            const readme =
                `EVIQ EARTH INITIATIVE\n` +
                `Founder Print Pack — Member No. ${padded}\n` +
                `${data.display_name} · ${data.founder_tier_label}\n\n` +
                `Contents\n--------\n` +
                `eviq-founder-${padded}-coin.png  · Personalized founding coin. 1254×1254 PNG, print-ready at 4 in / 300 DPI.\n` +
                `eviq-founder-${padded}-sticker.png · Roman-numeral car sticker. 1200×1200 PNG.\n` +
                `eviq-founder-${padded}-sticker.svg · Same sticker as vector, infinite scale.\n\n` +
                `Printing\n--------\n` +
                `Coin: matte or lightly textured stock, framed at 4"×4".\n` +
                `Sticker: outdoor-rated adhesive vinyl, contour-cut to the circle (~1.5 in).\n\n` +
                `Thank you for being one of the first.\n— The EVIQ Earth Initiative · eviq.pro\n`;

            const zip = new JSZip();
            zip.file(`eviq-founder-${padded}-coin.png`, coinBlob);
            zip.file(`eviq-founder-${padded}-sticker.png`, stickerPng);
            zip.file(`eviq-founder-${padded}-sticker.svg`, stickerSvg);
            zip.file("README.txt", readme);

            const zipBlob = await zip.generateAsync({ type: "blob" });
            const url = URL.createObjectURL(zipBlob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `eviq-founder-${padded}-print-pack.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            toast.success("Print Pack saved", {
                description: "Coin + window sticker + README, ready to print.",
            });
        } catch (e) {
            toast.error("Could not build Print Pack", { description: e.message });
        } finally {
            setBusyKit(false);
        }
    };

    if (notFound) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white">
                <div className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-10 text-center">
                    <h1 className="font-editorial text-2xl">Founder not found.</h1>
                    <Link
                        to="/dashboard"
                        className="mt-4 inline-flex items-center gap-2 text-sm text-[#a3e635]"
                    >
                        <ArrowLeft size={14} /> Back to dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[#050505] p-10 text-white">
                <div className="h-96 animate-pulse rounded-lg border border-[#1f1f22] bg-[#0a0a0a]" />
            </div>
        );
    }

    const padded = String(data.member_number).padStart(4, "0");
    const memberSince = new Date(data.member_since).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
    });

    return (
        <div
            data-testid="founder-profile-page"
            className="min-h-screen bg-[#050505] text-white"
        >
            <Toaster theme="dark" position="bottom-right" toastOptions={{
                style: { background: "#0a0a0a", border: "1px solid #27272a", color: "#fff" },
            }} />
            <header className="border-b border-[#1f1f22] bg-black/70 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100"
                    >
                        <ArrowLeft size={14} /> EVIQ Earth Initiative
                    </Link>
                    <button
                        type="button"
                        data-testid="founder-share-btn"
                        onClick={handleShare}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-3 py-1.5 text-xs text-[#a3e635] hover:bg-[#141a09]"
                    >
                        <ShareNetwork size={12} weight="bold" />
                        Copy share link
                    </button>
                </div>
            </header>

            <main className="mx-auto flex max-w-[1200px] flex-col gap-6 px-4 py-10 sm:gap-8 sm:px-6 lg:px-10 lg:py-14">
                {/* Hero */}
                <section className="relative overflow-hidden rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:p-10">
                    <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-[#a3e635]/10 blur-3xl" />
                    <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
                        <div>
                            <span className="eviq-chip">
                                {data.founder_tier_label} · {toRoman(new Date(data.member_since).getFullYear())}
                            </span>
                            <h1 className="mt-4 font-editorial text-4xl leading-tight text-white sm:text-5xl md:text-6xl">
                                {data.display_name}
                            </h1>
                            <p className="mt-3 font-mono-metric text-sm uppercase tracking-[0.24em] text-zinc-500">
                                Member No. {padded} · Since {memberSince}
                            </p>
                            <p className="mt-4 max-w-xl font-editorial text-lg italic text-zinc-300">
                                “Every mile with EVIQ is a mile toward restoration.”
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleShare}
                                    className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-4 py-2 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                                >
                                    <Copy size={12} weight="bold" />
                                    Copy this profile link
                                </button>
                                {data.founder_tier === "founding_100" && (
                                    <>
                                        <button
                                            type="button"
                                            data-testid="founder-download-coin"
                                            onClick={handleDownloadCoin}
                                            className="inline-flex items-center gap-2 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-4 py-2 text-sm text-[#a3e635] hover:bg-[#141a09]"
                                        >
                                            <DownloadSimple size={12} weight="bold" />
                                            Download my coin
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="founder-download-print-pack"
                                            onClick={handleDownloadPrintKit}
                                            disabled={busyKit}
                                            className="inline-flex items-center gap-2 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-4 py-2 text-sm text-[#a3e635] hover:bg-[#141a09] disabled:opacity-60"
                                        >
                                            <Package size={12} weight="bold" />
                                            {busyKit ? "Building…" : "Download Print Pack"}
                                        </button>
                                    </>
                                )}
                                <Link
                                    to="/dashboard"
                                    className="text-sm text-zinc-400 hover:text-zinc-100"
                                >
                                    Join EVIQ →
                                </Link>
                            </div>
                        </div>
                        <div className="hidden md:block">
                            <FoundingBadge
                                tier={data.founder_tier}
                                memberNumber={data.member_number}
                                size="lg"
                                testId="founder-profile-badge"
                            />
                        </div>
                    </div>
                </section>

                {/* Earth Score */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 text-center sm:p-8 lg:p-10">
                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                        Earth Score
                    </span>
                    <div className="mt-2 font-mono-metric text-6xl font-bold text-[#a3e635] sm:text-7xl md:text-8xl">
                        <CountUp end={data.earth_score} separator="," duration={2} />
                    </div>
                    <div className="mt-1 text-sm text-zinc-500">of 10,000</div>
                </section>

                {/* Personal stats */}
                <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <StatTile icon={Tree} color="#a3e635" label="Trees restored" value={data.trees_restored} />
                    <StatTile icon={Waves} color="#06b6d4" label="Ocean plastic (lbs)" value={data.ocean_plastic_lbs} />
                    <StatTile icon={BatteryFull} color="#8b5cf6" label="Batteries protected" value={data.batteries_protected} />
                    <StatTile
                        icon={Cpu}
                        color="#f59e0b"
                        label="Battery-years extended"
                        value={data.battery_life_extended_years}
                        suffix="yrs"
                        decimals={1}
                    />
                </section>

                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8">
                    <span className="eviq-chip">Attribution</span>
                    <h2 className="mt-3 font-editorial text-2xl text-white">
                        Verified by the EVIQ Earth Initiative.
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm text-zinc-400">
                        {data.display_name} is one of the {data.founder_tier_short.toLowerCase()}s
                        whose EVIQ membership quietly funds forest restoration, ocean cleanup,
                        battery recycling research, and veteran clean-energy careers — with no
                        donation prompts, ever.
                    </p>
                </section>

                <footer className="border-t border-[#1f1f22] py-8 text-center text-xs text-zinc-500">
                    eviq.pro · Earth Initiative
                </footer>
            </main>
        </div>
    );
};

export default FounderProfile;
